// Load the full appstate via React Query. Derived hooks (useProjects,
// useExpenses, etc.) read from this cache by default.
//
// Week 1 is read-only via this hook. Week 2 adds per-slice mutations.
// Week 1 gate just needs: sign in → see project count in console.

import { useQuery } from '@tanstack/react-query'
import { loadAppState, saveAppState } from '../lib/db'
import { catchUpRecurringSeeds } from '../lib/recurring'
import { qk } from './queryKeys'
import { useAuth } from './useAuth'
import type { AppState } from '../types/firestore'

// Guard so the rollover only runs once per session per uid. React Query
// dedupes queryFn, but this survives hard refetches during the session.
const recurSeen = new Set<string>()

async function loadAppStateWithRollover(uid: string): Promise<AppState> {
  const state = await loadAppState(uid)
  if (recurSeen.has(uid)) return state

  const { expenses, nextExpId, changed } = catchUpRecurringSeeds(state)
  if (!changed) {
    recurSeen.add(uid)
    return state
  }

  const patched: AppState = { ...state, expenses, nextExpId }

  // Persist before returning so the first paint reflects the rolled-up
  // expenses. saveAppState (not saveSlice) because we also touch nextExpId
  // on the meta doc. Any write failure is swallowed — the in-memory patch
  // still renders correctly and a retry will run on the next cold load.
  try {
    await saveAppState(uid, patched)
    recurSeen.add(uid)
  } catch (err) {
    // Leave recurSeen unset so a retry fires on the next query refresh.
    console.warn('[recurring] catch-up save failed; will retry next load', err)
  }

  return patched
}

export function useAppState() {
  // effectiveUid = account holder's uid when current user is a Partner (per
  // V1 togglePartnerAccess semantics). We do NOT gate on effectiveReady —
  // that would delay the first paint even for admins (where effectiveUid
  // always equals own uid) and was causing components that assume data is
  // loaded to flash an empty-state / auto-open the walkthrough. React
  // Query swaps to the owner-uid cache key automatically once the resolve
  // finishes; a Partner sees a brief flash of empty data before the owner
  // appstate paints in. Cheap price for keeping admins unaffected.
  const { effectiveUid, isReady } = useAuth()
  return useQuery<AppState>({
    queryKey: effectiveUid ? qk.appstate(effectiveUid) : ['appstate', 'anon'],
    queryFn: () => loadAppStateWithRollover(effectiveUid!),
    enabled: !!effectiveUid && isReady,
    staleTime: 60_000,     // 1 min — Firestore snapshot listener will override anyway
  })
}
